import { Flex, Button, Text, Tooltip } from '@chakra-ui/react';
import { SupplierListItem } from '@texas/api/endpoints/suppliersApi';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { Icons } from '@texas/components/shared/Icons';
import { AddVariantSupplierModal } from './AddVariantSupplierModal';
import { VariantBranchSupplier } from '@texas/api/endpoints/variantsApi';
import { TagWithTooltip } from '@texas/components/shared/TagWithTooltip';

export function SuppliersList({
  suppliers,
  variantId,
  branchId,
  branchName,
  branchIdentifier,
  onAdded,
  isPdVariant,
  setIsAddingToBranch,
}: {
  suppliers: SupplierListItem[];
  variantId: number;
  branchId: number;
  branchName: string;
  branchIdentifier?: string;
  onAdded?: (supplier: VariantBranchSupplier) => void;
  isPdVariant: boolean;
  setIsAddingToBranch?: (id: number | null) => void;
}) {
  const { value, isOpen, onClose, onOpen } =
    useValueDisclosure<SupplierListItem>();

  const [isAdding, setIsAdding] = useState<boolean>(false);

  return (
    <Flex flexDir="column" gap={2} p={2}>
      {suppliers.map((x) => (
        <SupplierListItemComponent
          supplier={x}
          key={x.id}
          onClick={() => onOpen(x)}
          isAdding={isAdding}
          isPdVariant={isPdVariant}
        />
      ))}
      {value && (
        <AddVariantSupplierModal
          supplier={value}
          variantId={variantId}
          branchId={branchId}
          branchName={branchName}
          branchIdentifier={branchIdentifier}
          isOpen={isOpen}
          onClose={onClose}
          onAdded={(supplier) => {
            setIsAdding(false);
            onAdded?.(supplier);
          }}
          setIsAddingToBranch={(id) => {
            setIsAdding(id !== null);
            setIsAddingToBranch?.(id);
          }}
        />
      )}
    </Flex>
  );
}

function SupplierListItemComponent({
  supplier,
  isAdding,
  onClick,
  isPdVariant,
}: {
  supplier: SupplierListItem;
  isAdding: boolean;
  onClick: () => void;
  isPdVariant: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Button
      isLoading={isAdding}
      loadingText={t('general.adding')}
      height="auto"
      py={2}
      isDisabled={supplier.alreadyAdded}
      onClick={onClick}
      alignItems="start"
      variant="solid"
      display="flex"
      flexDir="row"
      {...(supplier.alreadyAdded
        ? {
            borderLeft: '6px solid',
            borderLeftColor: 'texas.sand.100',
            _light: { borderLeftColor: 'texas.burntSienna.500' },
          }
        : null)}
    >
      <Flex alignItems="center" justifyContent="space-between" flexGrow={1}>
        <Flex
          direction="column"
          align="start"
          textAlign="start"
          whiteSpace="normal"
        >
          <Text variant="main">{supplier.name}</Text>
          {supplier.tags.length > 0 && (
            <Flex gap={1} py={1} wrap="wrap">
              {supplier.tags.map((tag) => (
                <TagWithTooltip key={tag.id} tag={tag} />
              ))}
            </Flex>
          )}
          <Flex gap={2}>
            {supplier.activeArticles > 0 && (
              <Text variant="small">
                {t('suppliers.activeArticles', {
                  count: supplier.activeArticles,
                })}
              </Text>
            )}

            {supplier.alreadyAdded && (
              <Text
                variant="small"
                color="texas.sand.100"
                _light={{ color: 'texas.burntSienna.500' }}
              >
                {!isPdVariant ? t('general.added') : t('general.connected')}
              </Text>
            )}
            {supplier.disabled && (
              <Tooltip label={t('variant.supplier.disabledTooltip')}>
                <Text
                  variant="small"
                  color="red.300"
                  _light={{ color: 'texas.burntSienna.500' }}
                >
                  {t('general.disabled')}
                </Text>
              </Tooltip>
            )}
          </Flex>
        </Flex>
        {supplier.isInternal && (
          <Tooltip label="Internal supplier">
            <Icons.HomeMapMarker boxSize={6} />
          </Tooltip>
        )}
      </Flex>
    </Button>
  );
}
